// AssignCompaniesAutocomplete.tsx
import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Chip, TextField, Tooltip, useTheme } from '@mui/material'
import RenderCompanyChip from '../RenderChipComponents/RenderCompanyChip'
import AddRenderOptionButton from '../Contacts/AddRenderOptionButton'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { usePostOrganization } from 'api/aws/organizations_table/usePostOrganization'
import { useEntitiesFromSearch } from 'api/search'

type Org = { id: string; name: string; org_uuid?: string; [k: string]: any }

function toNorm(o: any): Org | null {
  if (!o) return null
  const id = o.id ?? o.org_uuid ?? o.PK ?? null
  if (!id) return null
  return { id, name: o.name ?? '', org_uuid: o.org_uuid, ...o }
}

function uniqById(arr: Org[]) {
  const seen = new Set<string>()
  return arr.filter(o => {
    if (!o?.id) return false
    if (seen.has(o.id)) return false
    seen.add(o.id)
    return true
  })
}

export default function AssignCompaniesAutocomplete({
  label,
  value, // value is Org[] (objects), not IDs
  onChange, // onChange(context, string[] of IDs)
  onBlur, // onBlur(context, string[] of IDs)
  context = 'organizations',
  multiple = true,
  disabled = false,
}: {
  label?: string
  value: any[] // array of org-like objects (we'll normalize)
  onChange: (ctx: string, ids: string[]) => void
  onBlur: (ctx: string, ids: string[]) => void
  context?: string
  multiple?: boolean
  disabled?: boolean
}) {
  const { showSnackbar } = useSnackbarContext()
  const theme = useTheme()
  const { organizations } = useEntitiesFromSearch()
  const { mutate: createOrganization } = usePostOrganization()

  // Normalize options from hook
  const options: Org[] = useMemo(() => {
    return uniqById((organizations ?? []).map(toNorm).filter(Boolean) as Org[])
  }, [organizations])

  // Normalize incoming value (objects) → Org[]
  const normalizedValueFromProps: Org[] = useMemo(() => {
    return uniqById((value ?? []).map(toNorm).filter(Boolean) as Org[])
  }, [value])

  const [inputValue, setInputValue] = useState('')
  const [selected, setSelected] = useState<Org[]>(normalizedValueFromProps)

  // keep internal state in sync with parent props
  useEffect(() => {
    setSelected(normalizedValueFromProps)
  }, [normalizedValueFromProps])

  // Always emit IDs upward
  const emitIds = (arr: Org[]) => {
    const ids = arr.map(o => o.id).filter(Boolean)
    onChange(context, ids)
    onBlur(context, ids)
  }

  const handleSaveNew = (name: string) => {
    // Create org on server, then append to selection
    createOrganization(
      { name },
      {
        onSuccess: (data: any) => {
          const newOrg: Org = toNorm({ ...data, id: data.org_uuid, name: data.name })!
          const next = uniqById([...(selected ?? []), newOrg])
          setSelected(next)
          emitIds(next)
          showSnackbar('Organization created successfully', 'success')
        },
      }
    )
  }

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect
      freeSolo={false} // IMPORTANT: prevent strings entering value
      options={options}
      value={selected}
      onChange={(_, newValue) => {
        // Filter out anything invalid or our “add new” stub if user somehow selects it
        const clean: Org[] = uniqById((newValue as any[]).map(toNorm).filter(Boolean) as Org[])
        setSelected(clean)
        emitIds(clean)
      }}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      isOptionEqualToValue={(opt, val) => opt.id === val.id} // Consistent identity
      getOptionLabel={o => o?.name ?? ''}
      filterOptions={(opts, { inputValue }) => {
        const base = opts.filter(o => (o?.name ?? '').toLowerCase().includes(inputValue.toLowerCase()))
        // We DO NOT push a pseudo option into the list; we render a header/button instead.
        return base
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as any
        return (
          <Box component="li" key={option.id} {...rest}>
            <RenderCompanyChip option={option} />
          </Box>
        )
      }}
      renderInput={params => (
        <Box>
          <TextField {...params} label={label} autoComplete="off" />
          {/* Inline add-new button shown when no exact match exists */}
          {inputValue && !options.some(o => (o.name ?? '').toLowerCase() === inputValue.toLowerCase()) && (
            <AddRenderOptionButton
              props={{} as any}
              option={{ inputValue, specialOption: true }}
              handleOpenDialog={() => handleSaveNew(inputValue)}
            />
          )}
        </Box>
      )}
      renderTags={(tagValue, getTagProps) => {
        const maxVisibleChips = 1
        const extra = tagValue.slice(maxVisibleChips) as Org[]
        return (
          <>
            {tagValue.slice(0, maxVisibleChips).map((o, i) => {
              const { key, ...rest } = getTagProps({ index: i })
              return (
                <Box key={o.id} sx={{ maxWidth: '100%', overflow: 'hidden', m: 0 }}>
                  <Chip
                    key={key}
                    label={<RenderCompanyChip option={o} />}
                    {...rest}
                    onDelete={() => {
                      const next = selected.filter(x => x.id !== o.id)
                      setSelected(next)
                      emitIds(next)
                    }}
                    size="small"
                    sx={{
                      height: 'auto',
                      maxWidth: 220,
                      borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                      borderWidth: 0.5,
                      borderStyle: 'solid',
                      bgcolor: 'transparent',
                      color: theme.palette.mode === 'dark' ? 'white' : 'black',
                      borderRadius: '4px',
                      mr: 1,
                    }}
                  />
                </Box>
              )
            })}
            {extra.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    {extra.map(o => (
                      <RenderCompanyChip key={o.id} option={o} />
                    ))}
                  </Box>
                }
                arrow
              >
                <Chip
                  label={`+${extra.length}`}
                  size="small"
                  sx={{
                    height: 'auto',
                    borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    borderRadius: '4px',
                    mr: 1,
                  }}
                />
              </Tooltip>
            )}
          </>
        )
      }}
      fullWidth
      disabled={disabled}
    />
  )
}
